// Curated font registry for the print editor.
//
// `bundled: true` families ship as woff2 files in src/assets/fonts (see
// editorFonts.css) and render identically on every machine. The rest are
// system staples present on both Windows and macOS. Deliberately small:
// for a K-12 poster tool, fewer + better fonts beats a font-chaos list
// (and Lexend is included specifically as a dyslexia-friendly option).
import { cache } from 'fabric';
import '../../../assets/fonts/editorFonts.css';

export const EDITOR_FONTS = [
  // Bundled (OFL licensed)
  { family: 'Lexend', category: 'Sans', bundled: true, weights: [300, 400, 500, 600, 700, 800, 900] },
  { family: 'Nunito', category: 'Sans', bundled: true, weights: [300, 400, 500, 600, 700, 800, 900] },
  { family: 'Fredoka', category: 'Fun', bundled: true, weights: [300, 400, 500, 600, 700] },
  { family: 'Anton', category: 'Display', bundled: true, weights: [400] },
  { family: 'Bebas Neue', category: 'Display', bundled: true, weights: [400] },
  { family: 'Playfair Display', category: 'Serif', bundled: true, weights: [400, 500, 600, 700, 800, 900] },
  { family: 'Pacifico', category: 'Script', bundled: true, weights: [400] },
  { family: 'Patrick Hand', category: 'Handwritten', bundled: true, weights: [400] },
  // School-handwriting faces (all OFL), bundled for the handwriting worksheet
  // recipes so a district can pick its own style. The ruling re-measures per
  // font, so all three rule correctly; they differ in slant and in where that
  // puts the dashed midline. Slant is measured from the ink, not eyeballed.
  //
  //   Edu NSW ACT Foundation   +6.0deg  54.2%  <- handwriting-lines DEFAULT
  //   Edu QLD Beginner        +10.1deg  54.2%     AU / D'Nealian-style slant
  //   Andika                   +0.0deg  62.9%     fully upright option
  //
  // Both Edu faces are drawn for instruction, so their x-height lands at ~54%
  // of the ascender and the midline falls mid-row; NSW ACT is the default only
  // because it is the less-slanted of the two at an identical ratio, and US
  // manuscript instruction is upright. Andika is the only truly upright face,
  // but at 62.9% its midline rides high, so it is offered, not defaulted.
  // Our non-school handwritten faces measure 63-74%. See handwriting.ts.
  { family: 'Edu NSW ACT Foundation', category: 'Handwritten', bundled: true, weights: [400, 500, 600, 700] },
  { family: 'Edu QLD Beginner', category: 'Handwritten', bundled: true, weights: [400, 500, 600, 700] },
  { family: 'Andika', category: 'Handwritten', bundled: true, weights: [400] },
  // System staples (Windows + macOS)
  { family: 'Arial', category: 'Sans', bundled: false, weights: [400, 700] },
  { family: 'Arial Black', category: 'Display', bundled: false, weights: [400] },
  { family: 'Verdana', category: 'Sans', bundled: false, weights: [400, 700] },
  { family: 'Tahoma', category: 'Sans', bundled: false, weights: [400, 700] },
  { family: 'Trebuchet MS', category: 'Sans', bundled: false, weights: [400, 700] },
  { family: 'Georgia', category: 'Serif', bundled: false, weights: [400, 700] },
  { family: 'Times New Roman', category: 'Serif', bundled: false, weights: [400, 700] },
  { family: 'Courier New', category: 'Mono', bundled: false, weights: [400, 700] },
  { family: 'Comic Sans MS', category: 'Fun', bundled: false, weights: [400, 700] },
  { family: 'Impact', category: 'Display', bundled: false, weights: [400] },
];

export const FONT_FAMILIES = EDITOR_FONTS.map((f) => f.family);

// Load every bundled font into document.fonts so the Fabric canvas measures
// and rasterizes with the real face (canvas text does NOT trigger CSS font
// loading by itself — without this, the first text added in a bundled font
// renders in the fallback font until something else loads it).
let fontsLoadedPromise = null;
export function loadEditorFonts() {
  if (fontsLoadedPromise) return fontsLoadedPromise;
  const loads = [];
  for (const f of EDITOR_FONTS) {
    if (!f.bundled) continue;
    loads.push(document.fonts.load(`16px "${f.family}"`));
    if (f.weights.includes(700)) {
      loads.push(document.fonts.load(`700 16px "${f.family}"`));
    }
  }
  fontsLoadedPromise = Promise.allSettled(loads).then((results) => {
    // fabric v6 caches per-family glyph widths; any text measured before
    // these faces finished loading has fallback-font metrics cached. Drop
    // the bundled families' entries so callers' initDimensions() re-measure
    // with the real faces (clearing an unmeasured family is a no-op).
    for (const f of EDITOR_FONTS) {
      if (f.bundled) cache.clearFontCache(f.family);
    }
    return results;
  });
  return fontsLoadedPromise;
}
